/**
 * TaskActivityFeed — TASK-735.
 *
 * The full audit trail of a task, rendered read-only. Migration 197 (TASK-684) writes every
 * action on every task into `task_events`; this is the surface where a human reads it.
 *
 * Honesty rules:
 *  - the API returns events newest-first and this component NEVER re-sorts them;
 *  - `attempted` shows the recorded approach, outcome and failure reason — the whole point
 *    of the column;
 *  - `source_excerpt` / `source_locator` render through the shared ProvenanceChain, copied
 *    verbatim — a re-rendered quote is a fabricated quote;
 *  - an event with no detail is still a row: the trail is append-only, so is this list.
 */
import { useState } from 'react';
import { Bot, Cog, User } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

import { TASK_OUTCOME_COPY, type TaskEvent, type TaskOutcome } from '../../types/tasks';
import { ProvenanceChain, type ProvenanceStep } from '../provenance/ProvenanceChain';

/** Long trails collapse past this; the toggle shows the rest. */
const VISIBLE_CAP = 50;

const EVENT_LABELS: Record<string, string> = {
  created: 'Created',
  claimed: 'Claimed',
  attempted: 'Attempted',
  artifact_added: 'Artifact attached',
  artifact_withdrawn: 'Artifact withdrawn',
  status_changed: 'Status changed',
  outcome_recorded: 'Outcome recorded',
  signed_off: 'Signed off',
  dismissed: 'Dismissed',
};

function eventLabel(event: string): string {
  return EVENT_LABELS[event] ?? event.replace(/_/g, ' ');
}

/** `actor_kind` is typed human|agent, but the trail may carry `system` rows too. */
function actorKind(kind: string): { label: string; Icon: typeof Bot } {
  if (kind === 'agent') return { label: 'AI', Icon: Bot };
  if (kind === 'human') return { label: 'Human', Icon: User };
  return { label: 'System', Icon: Cog };
}

function EventRow({ event }: { event: TaskEvent }) {
  const { label: kindLabel, Icon } = actorKind(event.actor_kind);
  const detail = event.detail;
  const excerpt = event.source_excerpt?.trim();

  return (
    <li data-testid="task-event" className="border-t border-gray-100 pt-2 first:border-t-0 first:pt-0">
      <p className="flex flex-wrap items-center gap-1.5 text-xs text-gray-700">
        <Icon className="h-3 w-3 text-gray-400" aria-hidden="true" />
        <span className="font-medium">{event.actor}</span>
        <span
          data-testid="task-event-actor-kind"
          className="rounded bg-gray-100 px-1 text-[10px] font-medium text-gray-600"
        >
          {kindLabel}
        </span>
        <span>{eventLabel(event.event)}</span>
        {(event.from_status || event.to_status) && (
          <span data-testid="task-event-transition" className="text-gray-500">
            {[event.from_status, event.to_status].filter(Boolean).join(' → ')}
          </span>
        )}
        {event.at && (
          <span className="text-gray-400">
            {formatDistanceToNow(new Date(event.at), { addSuffix: true })}
          </span>
        )}
      </p>

      {/* The detail narrative — whatever of it was actually recorded, nothing invented. */}
      {detail && (
        <div className="mt-1 pl-4 text-xs text-gray-600">
          {typeof detail.approach === 'string' && (
            <p>
              <span className="text-gray-400">Approach: </span>
              {detail.approach}
            </p>
          )}
          {typeof detail.outcome === 'string' && (
            <p data-testid="task-event-outcome">
              <span className="text-gray-400">Outcome: </span>
              {/* TASK-922: `outcome_recorded` carries the enum, and the enum is what was
                  rendered — "investigation_complete_no_action" in an audit trail a
                  compliance officer reads. Falls through to the raw value for the OTHER
                  producer of this key: an agent's `attempted` event writes a free-text
                  outcome here, which has no entry in the map and must not be swallowed. */}
              {TASK_OUTCOME_COPY[detail.outcome as TaskOutcome]?.label ?? detail.outcome}
            </p>
          )}
          {/* TASK-922: the rationale the advisor typed. Recorded since TASK-724, shown
              nowhere — the "why" behind every signal disposition existed only in the
              database. */}
          {typeof detail.rationale === 'string' && detail.rationale && (
            <p data-testid="task-event-rationale">
              <span className="text-gray-400">Why: </span>
              {detail.rationale}
            </p>
          )}
          {/* What the outcome MEANT for the signal. The counter-intuitive half of TASK-724:
              none of the four closes a condition, and the trail should say so where the
              decision is read back. */}
          {typeof detail.effect_on_subject === 'string' && (
            <p className="text-gray-500">
              <span className="text-gray-400">Effect on the signal: </span>
              {detail.effect_on_subject.replace(/_/g, ' ')}
            </p>
          )}
          {typeof detail.summary === 'string' && (
            <p>
              <span className="text-gray-400">Summary: </span>
              {detail.summary}
            </p>
          )}
          {typeof detail.blocked_by === 'string' && detail.blocked_by && (
            <p data-testid="task-event-blocked" className="text-orange-800">
              <span className="text-gray-400">Blocked by: </span>
              {detail.blocked_by}
            </p>
          )}
        </div>
      )}

      {/* The stored citation, verbatim, through the one shared renderer. */}
      {excerpt && (
        <div className="mt-1 pl-4">
          <ProvenanceChain
            steps={[
              {
                excerpt,
                locator: (event.source_locator ?? null) as ProvenanceStep['locator'],
              },
            ]}
          />
        </div>
      )}
    </li>
  );
}

export function TaskActivityFeed({ events }: { events: TaskEvent[] }) {
  const [showAll, setShowAll] = useState(false);

  if (events.length === 0) {
    return <p className="text-xs text-gray-500">No activity recorded</p>;
  }

  const visible = showAll ? events : events.slice(0, VISIBLE_CAP);

  return (
    <div>
      <ul aria-label="Task activity" className="space-y-2">
        {visible.map((event) => (
          <EventRow key={event.id} event={event} />
        ))}
      </ul>
      {!showAll && events.length > VISIBLE_CAP && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-2 text-xs font-medium text-gray-500 underline underline-offset-2 hover:text-gray-700"
        >
          Show all {events.length}
        </button>
      )}
    </div>
  );
}

export default TaskActivityFeed;
